import React, { ChangeEvent, forwardRef, InputHTMLAttributes, useCallback, useRef } from 'react';
import { NumericFormat, NumericFormatProps } from 'react-number-format';
import { InputAffix, InputAffixProps } from './InputAffix';
import { InputBase, InputBaseProps, InputBaseType } from './InputBase';

// NOTE: 빌드시 포함될 수 있도록 react-number-format 타입을 복사
interface NumberFormatValues {
  floatValue: number | undefined;
  formattedValue: string;
  value: string;
}

type ChangeHandler<T extends boolean = false> = T extends true
  ? (value: number | null, event: ChangeEvent<HTMLInputElement>) => void
  : (value: number, event: ChangeEvent<HTMLInputElement>) => void;

export type NumericInputBaseType<TAllowEmpty extends boolean = false> = Omit<InputBaseType, 'showCounter'> &
  InputAffixProps & {
    value?: number | string | null;
    defaultValue?: number | string;
    /** @default true */
    allowNegative?: boolean;
    decimalScale?: number;
    allowEmpty?: TAllowEmpty;
    isAllowed?: (values: NumberFormatValues) => boolean;
    onChange?: ChangeHandler<TAllowEmpty>;
  };

export type NumericInputProps<TAllowEmpty extends boolean = false> = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'type' | 'onChange' | 'width' | 'size'
> &
  NumericInputBaseType<TAllowEmpty>;

function NumericInputInner<TAllowEmpty extends boolean = false>(
  props: NumericInputProps<TAllowEmpty>,
  ref: React.Ref<HTMLInputElement>,
): JSX.Element {
  const {
    className,
    status = 'info',
    prefix,
    suffix,
    disabled,
    size,
    onChange,
    defaultValue,
    value,
    allowNegative = true,
    allowEmpty = false,
    width,
    ...restProps
  } = props;

  const numberFormatRef = useRef<NumberFormatValues>();

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (allowEmpty) {
        const value = numberFormatRef.current?.floatValue ?? null;
        (onChange as ChangeHandler<true>)?.(value, event);
      } else {
        const value = numberFormatRef.current?.floatValue ?? 0;
        onChange?.(value, event);
      }
    },
    [onChange, allowEmpty],
  );

  const getCommonNumberFormatProps = (): NumericFormatProps<InputBaseProps> => ({
    allowNegative,
    customInput: InputBase,
    thousandSeparator: ',',
    getInputRef: ref,
    status,
    leftAddon: prefix,
    rightAddon: suffix,
    type: 'text',
    value,
    defaultValue,
    onValueChange: (values) => (numberFormatRef.current = values),
    onChange: handleChange,
    disabled,
    size,
    ...restProps,
  });

  return prefix || suffix ? (
    <InputAffix
      className={className}
      disabled={disabled}
      status={status}
      prefix={prefix}
      suffix={suffix}
      width={width}
      size={size}
    >
      <NumericFormat {...getCommonNumberFormatProps()} />
    </InputAffix>
  ) : (
    <NumericFormat {...getCommonNumberFormatProps()} className={className} width={width} />
  );
}

export const NumericInput = forwardRef(NumericInputInner) as <TAllowEmpty extends boolean = false>(
  props: NumericInputProps<TAllowEmpty> & { ref?: React.Ref<HTMLInputElement> },
) => JSX.Element;
